import React, { Fragment, useState } from "react";
import styles from "./signin.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { resendVerification, userLogin } from "../../../helpers";
import Button from "../../Shared/Button";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { authenticateUser } from "../../Redux/Reducer/authReducer";
import EyeClose from "../../../assets/images/eye-close.svg";
import EyeOpen from "../../../assets/images/eye-open.svg";
import AuthLayout from "../AuthLayout";
import AuthTextField from "../AuthTextField";
import AuthAlerts from "../Signup/AuthAlerts";
import CuteMascotSpinner from "../../../global/CuteMascotSpinner";

const SigninSchema = yup.object().shape({
  email: yup.string().email("Please enter valid email.").required("Required"),
  password: yup
    .string()
    .min(8, "Your password is not strong enough. Use at least 8 characters")
    .required("Required"),
});

const Signin = () => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")?.[0];
  const isSeller = subdomain?.includes("seller");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [signinError, setSigninError] = useState("");
  const [notVerified, setNotVerified] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: SigninSchema,
    onSubmit: (values) => {
      setLoading(true);
      loginAuth(values);
    },
  });

  const loginAuth = async (values) => {
    const submitData = {
      email: values?.email,
      password: values?.password,
      device_type: 1,
      device_token: process.env.REACT_APP_DEVICE_TOKEN,
      loginType: isSeller ? "seller" : "admin",
    };
    setSigninError("");

    userLogin(submitData)
      .then((responseData) => {
        setLoading(false);
        formik.setSubmitting(false);

        if (responseData.success) {
          const userDataObject = responseData?.data || {};
          const idToken = responseData?.id_token || "";
          const accessToken = responseData?.access_token || "";
          const userData = {
            idToken,
            accessToken,
            login: true,
            role: userDataObject?.role || "admin",
            id: userDataObject?.id || 0,
            email: userDataObject?.email || values?.email || "",
            name: userDataObject?.name || "",
            profileUrl: userDataObject?.profile_picture || "",
            loading: false,
          };
          toast.success("Logged in successfully");
          dispatch(authenticateUser(userData));
          navigate("/dashboard");
        } else {
          const responseObject = responseData?.response || {};
          const responseMessage = responseObject?.data?.message || "";
          const incorrectPassword = responseMessage === "Incorrect password.";
          const responseCode = responseObject?.status;
          const notAvailable = responseCode === 503;
          const notVerified =
            responseMessage?.includes("not been verified") &&
            responseCode === 404;
          const networkError = responseData?.code === "ERR_NETWORK";
          if (incorrectPassword) {
            setSigninError("Invalid email or password. Please try again");
          } else if (notAvailable) {
            setSigninError(
              "Lumi is currently unavailable. Please try again later."
            );
          } else if (networkError) {
            setSigninError(
              "Unable to connect to the server. Please check your internet connection and try again."
            );
          } else {
            if (notVerified) {
              setNotVerified(true);
            } else {
              setSigninError(responseMessage || "Something went wrong");
            }
          }
        }
      })
      .catch((err) => {
        dispatch(authenticateUser({ loading: false }));
        setSigninError(typeof err === "string" ? err : "Something went wrong");
      });
  };

  const handleResendVerification = async () => {
    const submitData = {
      email: formik?.values?.email,
    };
    try {
      const response = await resendVerification(submitData);
      toast.success(response?.message || "Email sent. Please check your inbox");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const emailDomain = `https://${formik?.values?.email?.split("@")[1]}`;
  return (
    <Fragment>
      {loading && <CuteMascotSpinner />}
      <AuthLayout>
        {notVerified ? (
          <>
            <AuthAlerts
              accountCreatedButNotVerified
              buttonOnClick={() => {
                window.open(emailDomain, "_blank");
              }}
              linkOnClick={() => {
                handleResendVerification();
              }}
            />
          </>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className={styles["login_container"]}>
              <h4 className={styles["login_title"]}>
                {isSeller ? "Seller" : "Admin"} Login
              </h4>
              <AuthTextField
                heading="Email"
                type="email"
                onChange={formik.handleChange}
                name="email"
                error={formik?.errors?.email || signinError}
                touched={formik?.touched?.email}
                disabled={loading}
                hideError={Boolean(signinError)}
              />
              <div className={`${styles["password_container"]}`}>
                <AuthTextField
                  heading="Password"
                  error={signinError ? signinError : formik?.errors?.password}
                  icon={togglePassword ? EyeOpen : EyeClose}
                  iconOnClick={() => {
                    setTogglePassword(!togglePassword);
                  }}
                  type={togglePassword ? "text" : "password"}
                  onChange={formik.handleChange}
                  name="password"
                  touched={signinError ? true : formik?.touched?.password}
                  disabled={loading}
                />
              </div>
              <Link
                to="/forgot-password"
                className={styles["forgot_password_link"]}
              >
                <div className={styles["forgot_password"]}>Forgot Password</div>
              </Link>

              <div className={styles["login_button_container"]}>
                <Button
                  text="Login"
                  className={styles["login_btn"]}
                  type="submit" // Ensure the button is of type "submit"
                  disabled={loading || formik.isSubmitting}
                />
              </div>
            </div>
          </form>
        )}
      </AuthLayout>
    </Fragment>
  );
};

export default Signin;

// import React, { Fragment, useState } from "react";
// import styles from "./signin.module.scss";
// import { Link, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { resendVerification, userLogin } from "../../../helpers";
// import Button from "../../Shared/Button";
// import { useFormik } from "formik";
// import * as yup from "yup";
// import { useDispatch } from "react-redux";
// import { authenticateUser } from "../../Redux/Reducer/authReducer";
// import EyeClose from "../../../assets/images/eye-close.svg";
// import EyeOpen from "../../../assets/images/eye-open.svg";
// import AuthLayout from "../AuthLayout";
// import AuthTextField from "../AuthTextField";
// import AuthAlerts from "../Signup/AuthAlerts";
// import CuteMascotSpinner from "../../../global/CuteMascotSpinner";

// const SigninSchema = yup.object().shape({
//   email: yup.string().email("Please enter valid email.").required("Required"),
//   password: yup
//     .string()
//     .min(8, "Your password is not strong enough. Use at least 8 characters")
//     .required("Required"),
// });

// const Signin = () => {
//   const hostname = window.location.hostname;
//   const subdomain = hostname.split(".")?.[0];
//   const isSeller = subdomain?.includes("seller");
//   const dispatch = useDispatch();
//   const [loading, setLoading] = useState(false);
//   const [signinError, setSigninError] = useState("");
//   const [notVerified, setNotVerified] = useState(false);

//   const [togglePassword, setTogglePassword] = useState(false);
//   const navigate = useNavigate();

//   const formik = useFormik({
//     initialValues: {
//       email: "",
//       password: "",
//     },
//     validationSchema: SigninSchema,
//     onSubmit: (values) => {
//       setLoading(true);
//       loginAuth(values);
//     },
//   });
//   // useEffect(() => {}, [value, name]);

//   const loginAuth = async (values) => {
//     const submitData = {
//       email: values?.email,
//       password: values?.password,
//       device_type: 1,
//       device_token: process.env.REACT_APP_DEVICE_TOKEN,
//       loginType: isSeller ? "seller" : "admin",
//     };
//     setSigninError("");

//     userLogin(submitData)
//       .then((responseData) => {
//         setLoading(false);
//         formik.setSubmitting(false);

//         if (responseData.success) {
//           const userDataObject = responseData?.data || {};
//           const idToken = responseData?.id_token || "";
//           const accessToken = responseData?.access_token || "";
//           const userData = {
//             idToken,
//             accessToken,
//             login: true,
//             role: userDataObject?.role || "admin",
//             id: userDataObject?.id || 0,
//             email: userDataObject?.email || values?.email || "",
//             name: userDataObject?.name || "",
//             profileUrl: userDataObject?.profile_picture || "",
//             loading: false, // Ensure loading is false on success
//           };
//           toast.success("Logged in successfully");
//           dispatch(authenticateUser(userData));
//           navigate("/dashboard");
//         } else {
//           const responseObject = responseData?.response || {};
//           const responseMessage = responseObject?.data?.message || "";
//           const incorrectPassword = responseMessage === "Incorrect password.";
//           const responseCode = responseObject?.status;
//           const notAvailable = responseCode === 503;
//           const notVerified =
//             responseMessage?.includes("not been verified") &&
//             responseCode === 404;
//           const networkError = responseData?.code === "ERR_NETWORK";
//           if (incorrectPassword) {
//             setSigninError("Invalid email or password. Please try again");
//           } else if (notAvailable) {
//             setSigninError(
//               "Lumi is currently unavailable. Please try again later."
//             );
//           } else if (networkError) {
//             setSigninError(
//               "Unable to connect to the server. Please check your internet connection and try again."
//             );
//           } else {
//             if (notVerified) {
//               setNotVerified(true);
//             } else {
//               setSigninError(responseMessage || "Something went wrong");
//             }
//           }
//         }
//       })
//       .catch((err) => {
//         // Set loading to false in case of an error
//         dispatch(authenticateUser({ loading: false }));
//         setSigninError(typeof err === "string" ? err : "Something went wrong");
//       });
//   };

//   const handleResendVerification = async () => {
//     const submitData = {
//       email: formik?.values?.email,
//     };
//     try {
//       const response = await resendVerification(submitData);
//       toast.success(response?.message || "Email sent. Please check your inbox");

//     } catch (error) {
//       toast.error("Something went wrong. Please try again.");
//     }
//   };

//   const emailDomain = `https://${formik?.values?.email?.split("@")[1]}`;
//   return (
//     <Fragment>
//       {loading && <CuteMascotSpinner />}
//       <AuthLayout>
//         {notVerified ? (
//           <>
//             <AuthAlerts
//               accountCreatedButNotVerified
//               buttonOnClick={() => {
//                 window.open(emailDomain, "_blank");
//               }}
//               linkOnClick={() => {
//                 handleResendVerification();
//               }}
//             />
//           </>
//         ) : (
//           <>
//             <div className={styles["login_container"]}>
//               <h4 className={styles["login_title"]}>
//                 {isSeller ? "Seller" : "Admin"} Login
//               </h4>
//               <AuthTextField
//                 heading="Email"
//                 type="email"
//                 onChange={formik.handleChange}
//                 name="email"
//                 error={formik?.errors?.email || signinError}
//                 touched={formik?.touched?.email}
//                 disabled={loading}
//                 hideError={Boolean(signinError)}
//               />
//               <div className={`${styles["password_container"]}`}>
//                 <AuthTextField
//                   heading="Password"
//                   error={signinError ? signinError : formik?.errors?.password}
//                   icon={togglePassword ? EyeOpen : EyeClose}
//                   iconOnClick={() => {
//                     setTogglePassword(!togglePassword);
//                   }}
//                   type={togglePassword ? "text" : "password"}
//                   onChange={formik.handleChange}
//                   name="password"
//                   touched={signinError ? true : formik?.touched?.password}
//                   disabled={loading}
//                 />
//               </div>
//               <Link
//                 to="/forgot-password"
//                 className={styles["forgot_password_link"]}
//               >
//                 <div className={styles["forgot_password"]}>Forgot Password</div>
//               </Link>

//               <div className={styles["login_button_container"]}>
//                 <Button
//                   text="Login"
//                   className={styles["login_btn"]}
//                   onClick={() => {
//                     formik.handleSubmit();
//                   }}
//                   disabled={loading || formik.isSubmitting}
//                 />
//               </div>

//             </div>
//           </>
//         )}
//       </AuthLayout>
//       {/* <ToastContainer /> */}
//     </Fragment>
//   );
// };

// export default Signin;
